import type { BankAccount } from "@freightsimple/generated-dashboard-openapi-client/models/BankAccount";
import type { CreditCard } from "@freightsimple/generated-dashboard-openapi-client/models/CreditCard";
import {
  isCreditCardExpired,
  isCreditCardExpiringSoon,
} from "@/Helpers/creditCardExpiry";
import type { ReactElement, ReactNode } from "react";
import { PlusCircleIcon } from "@phosphor-icons/react/PlusCircle";
import { BankLogo } from "@/Shared/Icons/BankLogo";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { CardBrandIcon } from "@freightsimple/ui-core/Display/CardBrandIcon";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import {
  Dropdown,
  type DropdownOption,
} from "@freightsimple/ui-core/Controls/Dropdown";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { T } from "@freightsimple/ui-core/Typography/T";

interface PaymentMethodsDropdownProps {
  cards: Array<CreditCard>;
  bankAccounts: Array<BankAccount>;
  isChequePaymentPermitted: boolean;
  isEFTPaymentPermitted: boolean;
  isETransferPaymentPermitted: boolean;
  isWirePaymentPermitted: boolean;
  selectedStripePaymentMethodId: string | undefined;
  onOptionSelected: (_: string) => void;
}

interface BuiltOption {
  option: DropdownOption;
  render: () => ReactNode;
}

function creditCardRow(cardInfo: CreditCard): BuiltOption {
  // Expiry comes from the backend as "M/YYYY" — the shared helper owns the
  // parsing and the expired definition. (The previous inline logic added 2000
  // to an already-4-digit year, so these warnings never showed.)
  const expired = isCreditCardExpired(cardInfo.expiry);
  const aboutToExpire = isCreditCardExpiringSoon(cardInfo.expiry, 1);

  return {
    option: {
      value: cardInfo.stripePaymentMethodId,
      label: `XXXX XXXX XXXX ${cardInfo.lastFourDigits}`,
      group: "Credit Cards",
      // Expired cards stay visible (so the customer understands why their
      // card isn't usable) but can't be selected for payment (ENG-4368)
      disabled: expired,
    },
    render: () => (
      <Row gap={3}>
        <CardBrandIcon size="md" brand={cardInfo.brand} />
        <T size="base" weight="normal">
          XXXX XXXX XXXX {cardInfo.lastFourDigits}
        </T>
        {expired ? (
          <T size="base" weight="light" color="red">
            Expired: {cardInfo.expiry}
          </T>
        ) : aboutToExpire ? (
          <T size="base" weight="light" color="red">
            Too close to expiry: {cardInfo.expiry}
          </T>
        ) : (
          <T size="base" weight="light" color="secondary">
            Expiry: {cardInfo.expiry}
          </T>
        )}
      </Row>
    ),
  };
}

function bankAccountRow(bankAccount: BankAccount): BuiltOption {
  return {
    option: {
      value: bankAccount.stripePaymentMethodId,
      // Include the bank name in the label so the closed-state trigger shows
      // it (e.g. "STRIPE TEST BANK — Account Ending 6789") — existing
      // `cy.contains("STRIPE TEST BANK")` selectors match the trigger text.
      label: `${bankAccount.bankName} — Account Ending ${bankAccount.lastFourDigits}`,
      group: "Bank Accounts",
    },
    render: () => (
      <Row gap={1}>
        <BankLogo
          institutionNumber={bankAccount.institutionNumber}
          stripeBankName={bankAccount.bankName}
        />
        <T size="base" weight="normal">
          Account Ending {bankAccount.lastFourDigits}
        </T>
      </Row>
    ),
  };
}

function otherRow(
  id: string,
  label: ReactNode | string,
  group: string,
): BuiltOption {
  return {
    option: {
      value: id,
      label: typeof label === "string" ? label : id,
      group,
    },
    render: () => (
      <T size="base" weight="normal">
        {label}
      </T>
    ),
  };
}

export function PaymentsMethodsDropdown(
  props: PaymentMethodsDropdownProps,
): ReactElement {
  const options: DropdownOption[] = [];
  const renderByValue = new Map<string, () => ReactNode>();

  function pushBuilt(b: BuiltOption) {
    options.push(b.option);
    renderByValue.set(b.option.value, b.render);
  }

  if (props.cards.length > 0) {
    props.cards.forEach((c) => pushBuilt(creditCardRow(c)));
  } else {
    pushBuilt(
      otherRow(
        "add-credit-card",
        <Button size="link" icon={PlusCircleIcon} iconWeight="fill">
          Add Credit Card
        </Button>,
        "Credit Cards",
      ),
    );
  }

  props.bankAccounts.forEach((b) => pushBuilt(bankAccountRow(b)));

  if (props.isChequePaymentPermitted) {
    pushBuilt(otherRow("cheque", "Pay by Cheque", "Other Methods"));
  }
  if (props.isEFTPaymentPermitted) {
    pushBuilt(otherRow("eft", "Pay by EFT/ACH", "Other Methods"));
  }
  if (props.isETransferPaymentPermitted) {
    pushBuilt(
      otherRow("etransfer", "Pay by Interac eTransfer", "Other Methods"),
    );
  }
  if (props.isWirePaymentPermitted) {
    pushBuilt(otherRow("wire", "Pay by Wire Transfer", "Other Methods"));
  }

  return (
    // The payments modal/review flows are still Cargo-styled and mount no
    // presentation provider — pin the underlined look this control has
    // always had. Width belongs to the caller's container.
    <ControlPresentationProvider presentation="underlined">
      <Dropdown
        placeholder="Select a payment method"
        value={props.selectedStripePaymentMethodId}
        onChange={props.onOptionSelected}
        options={options}
        width="full"
        data-testid="payment-methods-dropdown"
        renderOption={(opt) => {
          const r = renderByValue.get(opt.value);
          return r ? r() : <>{opt.label}</>;
        }}
      />
    </ControlPresentationProvider>
  );
}
